import React from 'react';
import { Grid, Box, Typography, Paper } from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import { StatCard } from './StatCard';

interface AssessmentDashboardProps {
    data: any;
}

const COLORS = ['#4caf50', '#8bc34a', '#ffc107', '#ff9800', '#f44336'];

export const AssessmentDashboard: React.FC<AssessmentDashboardProps> = ({ data }) => {
    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">Keine Daten verfügbar</Typography>
            </Box>
        );
    }

    const { summary, scoreDistribution } = data;

    const passFailData = [
        { name: 'Bestanden', value: summary?.passed || 0 },
        { name: 'Nicht bestanden', value: summary?.failed || 0 },
    ];

    return (
        <Box sx={{ px: 2 }}>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Gesamt Versuche"
                        value={summary?.total_attempts || 0}
                        icon={<AssessmentIcon sx={{ fontSize: 48 }} />}
                        color="primary"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Bestanden"
                        value={summary?.passed || 0}
                        subtitle={`${parseFloat(summary?.pass_rate || 0).toFixed(1)}% Bestehensquote`}
                        icon={<CheckCircleIcon sx={{ fontSize: 48 }} />}
                        color="success"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Nicht bestanden"
                        value={summary?.failed || 0}
                        icon={<CancelIcon sx={{ fontSize: 48 }} />}
                        color="error"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Ø Score"
                        value={summary?.avg_score ? `${parseFloat(summary.avg_score).toFixed(1)}` : '-'}
                        subtitle={`Min: ${summary?.min_score || '-'} / Max: ${summary?.max_score || '-'}`}
                        icon={<StarIcon sx={{ fontSize: 48 }} />}
                        color="info"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Pass/Fail Pie Chart */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Bestanden vs. Nicht bestanden
                        </Typography>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={passFailData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={5}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                                >
                                    <Cell fill="#4caf50" />
                                    <Cell fill="#f44336" />
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Score Distribution */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Score-Verteilung
                        </Typography>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={scoreDistribution || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="score_range" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" name="Anzahl" fill="#1976d2">
                                    {(scoreDistribution || []).map((entry: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Additional Metrics */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Weitere Metriken
                </Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="primary">
                                {summary?.min_score || '-'}
                            </Typography>
                            <Typography color="textSecondary">Minimale Punktzahl</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="primary">
                                {summary?.max_score || '-'}
                            </Typography>
                            <Typography color="textSecondary">Maximale Punktzahl</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography variant="h4" color="primary">
                                {summary?.avg_attempts ? parseFloat(summary.avg_attempts).toFixed(1) : '-'}
                            </Typography>
                            <Typography color="textSecondary">Ø Versuche pro Nutzer</Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default AssessmentDashboard;
